import React, { useEffect, useMemo, useRef } from "react";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import MessageBubble from "./MessageBubble";
import DateDivider from "./DateDivider";
import { ChatThreadSkeleton } from "./InboxSkeletons";

function formatDateLabel(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

export default function ChatWindow({
  messages,
  isLoading,
  typingUser,
  conversationSelected = true,
  channelsLoading = false,
  unconfiguredMediums = [],
  onOpenChannelSetup,
  canConfigureChannels = false,
}) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const grouped = useMemo(() => {
    const items = [];
    let lastDate = "";
    for (const msg of messages) {
      const label = formatDateLabel(msg.created_at);
      if (label && label !== lastDate) {
        items.push({ type: "date", label, key: `date-${label}-${msg.id}` });
        lastDate = label;
      }
      items.push({ type: "message", data: msg, key: `msg-${msg.id}` });
    }
    return items;
  }, [messages]);

  if (!conversationSelected) {
    const showSetup =
      !channelsLoading && Array.isArray(unconfiguredMediums) && unconfiguredMediums.length > 0;

    if (channelsLoading) {
      return (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#94a3b8",
          }}
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: 48, opacity: 0.6 }} />
          <span style={{ marginTop: 8, fontSize: 14 }}>Loading channel status…</span>
        </div>
      );
    }

    if (showSetup) {
      return (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 24px",
            textAlign: "center",
            color: "#64748b",
          }}
        >
          <WarningAmberOutlinedIcon sx={{ fontSize: 44, color: "#f59e0b" }} />
          <span style={{ marginTop: 12, fontSize: 15, fontWeight: 600, color: "#334155" }}>
            Messaging mediums are not configured
          </span>
          <p style={{ margin: "10px 0 0", fontSize: 13, lineHeight: 1.5, maxWidth: 360 }}>
            Connect at least WhatsApp (and optionally email or SMS) so inbound and outbound messages work from this
            inbox.
          </p>
          <ul
            style={{
              margin: "12px 0 0",
              padding: 0,
              listStyle: "none",
              fontSize: 13,
              color: "#64748b",
            }}
          >
            {unconfiguredMediums.map((name) => (
              <li key={name} style={{ marginBottom: 4 }}>
                <span style={{ color: "#dc2626", fontWeight: 600 }}>•</span> {name}
              </li>
            ))}
          </ul>
          {canConfigureChannels && typeof onOpenChannelSetup === "function" ? (
            <button
              type="button"
              onClick={onOpenChannelSetup}
              style={{
                marginTop: 18,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 18px",
                fontSize: 14,
                fontWeight: 600,
                color: "#fff",
                background: "#7c3aed",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
              }}
            >
              <SettingsOutlinedIcon sx={{ fontSize: 20 }} />
              Click to configure
            </button>
          ) : (
            <p style={{ marginTop: 16, fontSize: 12, color: "#94a3b8" }}>
              Ask an administrator to connect WhatsApp or other channels.
            </p>
          )}
          <span style={{ marginTop: 20, fontSize: 13, color: "#94a3b8" }}>
            Or select a conversation on the right when channels are ready.
          </span>
        </div>
      );
    }

    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#94a3b8",
        }}
      >
        <ChatBubbleOutlineIcon sx={{ fontSize: 48 }} />
        <span style={{ marginTop: 8, fontSize: 14 }}>Select a conversation to view messages</span>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <ChatThreadSkeleton />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#94a3b8",
        }}
      >
        <ChatBubbleOutlineIcon sx={{ fontSize: 48 }} />
        <span style={{ marginTop: 8, fontSize: 14 }}>No messages yet</span>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        padding: "8px 0",
        background: "#f8fafc",
      }}
    >
      {grouped.map((item) =>
        item.type === "date" ? (
          <DateDivider key={item.key} label={item.label} />
        ) : (
          <MessageBubble key={item.key} message={item.data} />
        )
      )}

      {typingUser && (
        <div style={{ padding: "4px 20px", fontSize: 12, color: "#64748b", fontStyle: "italic" }}>
          {typingUser} is typing...
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
